import React from "react";
import BackgroundCover from "../../components/backgroundCover/BackgroundCover";
import TitleWithDescription from "../../components/TitleWithDescription/TitleWithDescription";
import TimelineSection from "../../components/TimelineSection/TimelineSection";
import Title from "../../components/Title/Title";
import { isMobile } from "../../utils/appUtils";
import { useLanguage } from "../../context/LanguageContext";
import { philosophyTranslations, PhilosophyTranslations } from "../../translations/philosophyTemplate";
import { useEditMode } from "../../context/EditModeContext";
import EditableText from "../../components/EditableText/EditableText";
import { usePageContent } from "../../hooks/usePageContent";
import { validateContent } from "../../utils/contentValidator";

const Philosophy: React.FC = () => {
  const { language } = useLanguage();
  const { isEditMode } = useEditMode();
  const { content, loading } = usePageContent<PhilosophyTranslations>('philosophy');

  // Use translations as fallback content
  const fallbackContent = philosophyTranslations[language];
  
  // Validate content structure before rendering
  // This will throw an error if validation fails
  const contentToUse = content || fallbackContent;
  
  // Validate content against the expected schema
  try {
    validateContent(contentToUse, fallbackContent);
  } catch (error) {
    console.error('Content validation failed:', error);
    throw error; // Re-throw to prevent rendering with invalid content
  }

  // Show loading state if needed
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen" data-testid="loading-indicator">
        <div className="animate-spin h-12 w-12 border-2 border-gray-400 rounded-full border-t-transparent"></div>
      </div>
    );
  }

  // Use the validated content
  const t = contentToUse;

  return (
    <div className="min-h-screen bg-white">
      <BackgroundCover
        backgroundHeight={isMobile() ? 400 : undefined}
        imagePosition="center"
        backgroundImage="/images/philosophy_vision/philosophy_background.jpg"
        isWatchPage={true}
      >
        <div className="absolute top-1/2 left-[100px] -translate-y-[200px] z-10 hidden md:block">
          <Title
            firstLine={isEditMode ? (
              <EditableText
                contentKey="philosophy.title.firstLine"
                defaultValue={t.title.firstLine}
              />
            ) : t.title.firstLine}
            secondLine={isEditMode ? (
              <EditableText
                contentKey="philosophy.title.secondLine"
                defaultValue={t.title.secondLine}
              />
            ) : t.title.secondLine}
            className="hidden md:block"
            animate={true}
          />
        </div>
      </BackgroundCover>

      <div className="block md:hidden px-4 pt-8">
        <Title
          firstLine={isEditMode ? (
            <EditableText
              contentKey="philosophy.title.firstLine"
              defaultValue={t.title.firstLine}
            />
          ) : t.title.firstLine}
          secondLine={isEditMode ? (
            <EditableText
              contentKey="philosophy.title.secondLine"
              defaultValue={t.title.secondLine}
            />
          ) : t.title.secondLine}
          className=""
          animate={true}
        />
      </div>

      <div className="w-full px-4 md:px-0 py-8 md:py-16">
        <div className="flex flex-col items-center">
          <div className="px-4 md:px-24 lg:px-64 text-sm text-gray-600 leading-relaxed space-y-4">
            {t.mainContent.intro.map((paragraph: string, index: number) => (
              <p key={index} className={index === 0 ? "text-[46px] block mb-2 font-sans font-extralight uppercase leading-[1.02]" : ""}>
                {isEditMode ? (
                  <EditableText
                    contentKey={`philosophy.mainContent.intro.${index}`}
                    defaultValue={paragraph}
                    multiline={index !== 0}
                  />
                ) : paragraph}
              </p>
            ))}
          </div>
        </div>
      </div>

      <div className="relative w-full">
        <div className="relative w-full bg-gradient-to-b from-[#E1D8D3] via-[#FFFFFF] to-[#E1D8D3] py-8 md:py-12">
          <div className="mx-auto px-4 md:px-12 space-y-8 md:space-y-12">
            <div className="px-4 md:px-0">
              <TimelineSection
                title={{
                  firstLine: isEditMode ? (
                    <EditableText
                      contentKey="philosophy.mainContent.constantChange.title.firstLine"
                      defaultValue={t.mainContent.constantChange.title.firstLine}
                    />
                  ) : t.mainContent.constantChange.title.firstLine,
                  secondLine: isEditMode ? (
                    <EditableText
                      contentKey="philosophy.mainContent.constantChange.title.secondLine"
                      defaultValue={t.mainContent.constantChange.title.secondLine}
                    />
                  ) : t.mainContent.constantChange.title.secondLine,
                }}
                description={(
                  <div className="space-y-4">
                    {t.mainContent.constantChange.content.map((paragraph: string, index: number) => (
                      <p key={index} className={index === 1 ? "text-left text-sm font-medium" : ""}>
                        {isEditMode ? (
                          <EditableText
                            contentKey={`philosophy.mainContent.constantChange.content.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ) : paragraph}
                      </p>
                    ))}
                    <ul className="list-disc pl-8 space-y-2">
                      {t.mainContent.constantChange.listItems.map((item: string, index: number) => (
                        <li key={index}>
                          {isEditMode ? (
                            <EditableText
                              contentKey={`philosophy.mainContent.constantChange.listItems.${index}`}
                              defaultValue={item}
                            />
                          ) : item}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-4">
                      {isEditMode ? (
                        <EditableText
                          contentKey="philosophy.mainContent.constantChange.conclusion"
                          defaultValue={t.mainContent.constantChange.conclusion}
                          multiline={true}
                        />
                      ) : t.mainContent.constantChange.conclusion}
                    </p>
                  </div>
                )}
                media={{
                  type: 'image',
                  src: "/images/philosophy_vision/philosophy_sub1.jpg",
                  alt: "Time and Change"
                }}
                mediaPosition="right"
              />
            </div>

            <div className="px-4 md:px-0">
              <TimelineSection
                title={{
                  firstLine: isEditMode ? (
                    <EditableText
                      contentKey="philosophy.mainContent.mysticWorlds.title.firstLine"
                      defaultValue={t.mainContent.mysticWorlds.title.firstLine}
                    />
                  ) : t.mainContent.mysticWorlds.title.firstLine,
                  secondLine: isEditMode ? (
                    <EditableText
                      contentKey="philosophy.mainContent.mysticWorlds.title.secondLine"
                      defaultValue={t.mainContent.mysticWorlds.title.secondLine}
                    />
                  ) : t.mainContent.mysticWorlds.title.secondLine,
                }}
                description={(
                  <div className="space-y-4">
                    {t.mainContent.mysticWorlds.content.map((paragraph: string, index: number) => (
                      <p key={index}>
                        {isEditMode ? (
                          <EditableText
                            contentKey={`philosophy.mainContent.mysticWorlds.content.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ) : paragraph}
                      </p>
                    ))}
                  </div>
                )}
                media={{
                  type: 'image',
                  src: "/images/philosophy_vision/philosophy_sub2.jpg",
                  alt: "Mystic Worlds"
                }}
                mediaPosition="left"
              />
            </div>

            <div className="px-4 md:px-0">
              <TimelineSection
                title={{
                  firstLine: isEditMode ? (
                    <EditableText
                      contentKey="philosophy.mainContent.innovativeSpirit.title.firstLine"
                      defaultValue={t.mainContent.innovativeSpirit.title.firstLine}
                    />
                  ) : t.mainContent.innovativeSpirit.title.firstLine,
                  secondLine: isEditMode ? (
                    <EditableText
                      contentKey="philosophy.mainContent.innovativeSpirit.title.secondLine"
                      defaultValue={t.mainContent.innovativeSpirit.title.secondLine}
                    />
                  ) : t.mainContent.innovativeSpirit.title.secondLine,
                }}
                description={(
                  <div className="space-y-4">
                    {t.mainContent.innovativeSpirit.content.map((paragraph: string, index: number) => (
                      <p key={index}>
                        {isEditMode ? (
                          <EditableText
                            contentKey={`philosophy.mainContent.innovativeSpirit.content.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ) : paragraph}
                      </p>
                    ))}
                  </div>
                )}
                media={{
                  type: 'image',
                  src: "/images/philosophy_vision/philosophy_sub3.jpg",
                  alt: "Innovation"
                }}
                mediaPosition="right"
              />
            </div>

            <div className="px-4 md:px-0">
              <TimelineSection
                title={{
                  firstLine: isEditMode ? (
                    <EditableText
                      contentKey="philosophy.mainContent.brandIdentity.title.firstLine"
                      defaultValue={t.mainContent.brandIdentity.title.firstLine}
                    />
                  ) : t.mainContent.brandIdentity.title.firstLine,
                  secondLine: isEditMode ? (
                    <EditableText
                      contentKey="philosophy.mainContent.brandIdentity.title.secondLine"
                      defaultValue={t.mainContent.brandIdentity.title.secondLine}
                    />
                  ) : t.mainContent.brandIdentity.title.secondLine,
                }}
                description={(
                  <div className="space-y-4">
                    {t.mainContent.brandIdentity.content.map((paragraph: string, index: number) => (
                      <p key={index}>
                        {isEditMode ? (
                          <EditableText
                            contentKey={`philosophy.mainContent.brandIdentity.content.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ) : paragraph}
                      </p>
                    ))}
                  </div>
                )}
                media={{
                  type: 'image',
                  src: "/images/philosophy_vision/philosophy_sub4.jpg",
                  alt: "Brand Identity"
                }}
                mediaPosition="left"
                showLine={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Philosophy;
